import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'
import { Dist } from './Dist';
import Graphics, { mapCategoryColors, updateCameraToScene } from './Graphics';
import { mapMultiply, mapMerge, mapSum } from './utility'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { ALayer, appdata, buckets } from './internalModules';

export class WallType {
  protected className: string //string representation of class name
  guid: string
  name: string
  gwpsf: number
  gwpsfByMaterial: Map<string, number>
  height: Dist
  width: Dist
  length: Dist
  layers: ALayer[]
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  orbitControls: OrbitControls | undefined
  protected _exploded: boolean //is the graphic exploded
  protected _explodeAmount: number //how many feet each layer is exploded
  protected _wwr: number
  protected _colorByCategory: boolean

  set wwr(value: number) {
    this._wwr = value
  }
  get wwr(): number {return this._wwr}

  set exploded(value: boolean) {
    this._exploded = value
    if (this._exploded) {
      this._explodeAmount = Graphics.explodeAmount
    }
    else {
      this._explodeAmount = 0
    }
    this.regenSubtypes()
    this.regenGeometry()
  }
  get exploded() {return this._exploded}
  
  set colorByCategory(value: boolean) {
    this._colorByCategory = value
    this.regenSubtypes()
    this.regenGeometry()
  }

  get colorByCategory() {return this._colorByCategory}

  get area(): number {
    return this.height.toFeet() * this.width.toFeet()
  }

  get layerCount(): number {
    return this.getLayers(buckets.wall).length
      + this.getLayers(buckets.glazed).length
      + this.getLayers(buckets.hSolid).length
      + this.getLayers(buckets.hFraming).length
      + this.getLayers(buckets.column).length
  }

  constructor(height: Dist, width: Dist){
    this.className = 'WallType'
    this.guid = uuidv4()
    this.name = ''
    this.gwpsf = 0
    this.gwpsfByMaterial = new Map<string,number>()
    this.layers = []
    this.height = height
    this.width = width
    this.length = Dist.zero
    this._exploded = false
    this._explodeAmount = 0.02 // 1/4" default separation of layers
    this._wwr = 0.05
    this._colorByCategory = false
    this.scene = Graphics.getNewScene()
    this.camera = Graphics.getNewPerspectiveCamera()
    this.orbitControls = undefined
  }

  /** add a layer to this wall assembly*/ 
  addLayer(layerbucket: string, layer: ALayer) {
    if(layerbucket === buckets.wall){
      this.layers.push(layer)
      this.calculateGwpSF()
      this.regenGeometry()
    }
  }

  /** add a new layer to this wall assembly */
  addNewLayer(layerbucket: string, layerkind: string){
    let newlayer = new ALayer()
    newlayer.kind = layerkind
    this.addLayer(layerbucket, newlayer)
  }

  /**update a layer in the specified bucket */
  updateLayer(layerbucket: string, layer: ALayer, index: number){
      this.layers.splice(index, 1, layer)
      this.calculateGwpSF()
      this.regenGeometry()
  }

  /** remove a layer from this wall assembly*/
  removeLayer(layerbucket: string, index: number){
    if(layerbucket === buckets.wall){
      this.layers.splice(index, 1)
      this.calculateGwpSF()
      this.regenGeometry()
    }
  }

  /** method to reorder a layer in this wall assembly*/
  reorderLayer(layerbucket: string, fromIndex: number, toIndex: number) {
    if(layerbucket === buckets.wall){
      let layer = this.layers[fromIndex]
      this.layers.splice(fromIndex, 1)
      this.layers.splice(toIndex, 0, layer)
      this.regenGeometry()
    }
  }

  /** returns a copy of this wall assembly */
  getCopy(): WallType {
    let wt = new WallType(this.height, this.width)
    for (const layer of this.layers) {
      wt.layers.push(layer)
    }
    wt.exploded = this._exploded
    return wt
  }

  //method to update module height
  updateHeight(ht: Dist) {
    this.height = ht
    this.calculateGwpSF()
    this.regenGeometry()
  }

  //method to update module width
  updateWidth(wd: Dist) {
    this.width = wd
    this.calculateGwpSF()
    this.regenGeometry()
  }

 /** calculate GWP per SF and store in gwpsf property*/
  calculateGwpSF(){
    this.gwpsfByMaterial.clear()
    for (const layer of this.layers) {
      //add the material-gwp key-values for each layer
      mapMerge(this.gwpsfByMaterial, layer.getMaterialGwpMap(this))
    }
    //store total gwp per sf from all materials
    this.gwpsf = mapSum(this.gwpsfByMaterial) / this.area
    //divide each gwp in the map by the area
    mapMultiply(this.gwpsfByMaterial, 1 / this.area)
  }

  /** regenerate subtypes for ribbon, window, and curtain walls */
  regenSubtypes(){
  }

  //method to generate geometry for each layer
  //and add to this wall type scene
  regenGeometry() {
    Graphics.clearSceneMeshes(this.scene)
    if(this.layerCount < 1){
      return
    }
    this.scene.add(this.getMesh(0, this._explodeAmount)[0])
    this.colorMeshes()
    updateCameraToScene(this.camera, this.scene, this.orbitControls)
  }

  //method to return geometry of each layer
  getMesh(startingOffset = 0, explodeAmount: number): [THREE.Mesh, number] {
    let parentMesh = new THREE.Mesh()
    let zOffset = startingOffset
    for (const layer of this.layers) {
      const mesh = layer.getMesh(this)
        mesh.position.z = zOffset + (layer.totalThickness.ft / 2)
        parentMesh.add(mesh)
        zOffset = zOffset + layer.totalThickness.ft + explodeAmount
    }
    return [parentMesh, zOffset]
  }

 //method to return geometry of each layer
  getMeshCurtainWall(startingOffset = 0, explodeAmount: number): [THREE.Mesh, number] {
    let parentMesh = new THREE.Mesh()
    let zOffset = startingOffset
    const reverseLayers = Array.from(this.layers).reverse()
    for (const layer of reverseLayers) {
      const mesh = layer.getMesh(this)
        mesh.position.z = zOffset - (layer.totalThickness.ft / 2)
        parentMesh.add(mesh)
        zOffset = zOffset - layer.totalThickness.ft - explodeAmount
    }
    return [parentMesh, zOffset]
  }

  //method to color meshes
  colorMeshes() {
    if (this.colorByCategory) {
      this.colorMeshesByCategory()
    }
    else {
      this.colorMeshesByIntensity()
    }
  }

  //method to color meshes by gwp intensity of material
  colorMeshesByIntensity() {
    const materialMap = this.gwpsfByMaterial
    this.scene.traverse( (mesh: THREE.Object3D<THREE.Event>) => {
      if (mesh instanceof THREE.Mesh) {
        for (const matName of materialMap.keys()) {
          if (mesh.name === matName) {
            const intensity = materialMap.get(matName)! / mapSum(materialMap)
            mesh.material = Graphics.getMaterialColorScaled(mesh.material, intensity)
          }
        }
      }
    })
  }
  //method to color meshes by category
  colorMeshesByCategory() {
    this.scene.traverse( (mesh: THREE.Object3D<THREE.Event>) => {
      if (mesh instanceof THREE.Mesh && appdata.getMaterialNames().includes(mesh.name)) {
        const mat = appdata.getMaterial(mesh.name)
        mesh.material = Graphics.getMaterialColorCategory(mesh.material, mat.category)
      }
    })
  }

  /**returns the array of layers of the specified bucket */
  getLayers(bucket: string): ALayer[] {
    let layers: ALayer[] = []
    if(bucket === buckets.wall){
      layers = this.layers
    }
    return layers
  }

  /**returns all layers */
  getAllLayers(): ALayer[] {
    return this.getLayers(buckets.wall)
  }

  //custom method to convert to json
  toJSON(): object {
    return {
      className: this.className,
      guid: this.guid,
      name: this.name,
      height: this.height.ft.toString(),
      width: this.width.ft.toString(),
      length: '0',
      layers: this.layers,
      layersGlazed: [],
      layersFraming: [],
      layersColumn: [],
      wwr: this._wwr.toString()
    }
  }

}